import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, merge, Observable, of, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, skip} from 'rxjs/operators';
import {RequestsService} from './requests.service';
import {FaehigkeitenService} from './faehigkeiten.service';
import {RequestLanguage, requestLanguageFor, Requests, RequestType} from '../shared/model/request.model';
import {IFaehigkeit} from '../shared/model/faehigkeit.model';
import {
  BilingualName,
  BilingualSkill,
  inLanguage,
  skillCategory,
  skillName,
  sortByDisplayName,
  vocabularyName,
} from '../shared/model/vocabulary-name';

export type VocabularyKind = 'industry' | 'mainFocus' | 'method' | 'skill' | 'skillCategory';

export type StoredIndustry = string | (BilingualName & {id?: string | null});

interface Lookup {
  byId: Map<string, BilingualName>;
  byName: Map<string, BilingualName>;
}

const METHOD_CATEGORY = 'Vorgehensmodelle';
const PREFETCHED = ['industry', 'mainFocus', 'method'] as const;

function nameKey(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

function lookupOf(entries: (BilingualName & {id?: string | null})[]): Lookup {
  const byId = new Map<string, BilingualName>();
  const byName = new Map<string, BilingualName>();
  for (const entry of entries) {
    if (entry.id) byId.set(entry.id, entry);
    if (entry.name?.trim()) byName.set(nameKey(entry.name), entry);
  }
  return {byId, byName};
}

/**
 * Der angezeigte Name von Branchen, Schwerpunkten, Methodik und Fähigkeiten in der Oberflächensprache.
 *
 * Gespeichert wird weiterhin der deutsche Name. Branchen, Schwerpunkte und Methodik liegen im Profil als
 * Kopie oder als reiner Namensstring; aufgelöst wird deshalb über die zentrale Liste, damit eine später
 * ergänzte Übersetzung auch in Bestandsprofilen wirkt. Fehlt der zentrale Eintrag, bleibt der gespeicherte.
 * Fähigkeiten kommen vom Backend bereits aufgelöst.
 */
@Injectable({providedIn: 'root'})
export class VocabularyService {
  private readonly languageSubject: BehaviorSubject<RequestLanguage>;
  private readonly loaded = new Subject<void>();
  private readonly lookups = new Map<VocabularyKind, Lookup>();

  readonly language$: Observable<RequestLanguage>;
  /** Sprachwechsel oder eine nachgeladene zentrale Liste — angezeigte Namen, Sortierung und Suche neu berechnen. */
  readonly changes$: Observable<void>;

  constructor(
    translate: TranslateService,
    private requestsService: RequestsService,
    private skillService: FaehigkeitenService,
  ) {
    this.languageSubject = new BehaviorSubject(
      requestLanguageFor(translate.getCurrentLang?.() ?? translate.getDefaultLang?.()),
    );
    this.language$ = this.languageSubject.pipe(distinctUntilChanged());
    this.changes$ = merge(this.language$.pipe(skip(1), map(() => undefined)), this.loaded);
    translate.onLangChange?.subscribe(({lang}) => this.languageSubject.next(requestLanguageFor(lang)));
    // Auf Deutsch ist der gespeicherte Name schon der richtige; auf Englisch früh laden, damit er nicht erst nachspringt.
    this.language$.pipe(filter(language => language === 'en')).subscribe(() => PREFETCHED.forEach(kind => this.lookup(kind)));
  }

  get language(): RequestLanguage {
    return this.languageSubject.value;
  }

  industryName(stored: StoredIndustry | null | undefined): string {
    if (stored == null) return '';
    const lookup = this.lookup('industry');
    if (typeof stored === 'string') {
      return this.resolved(lookup?.byName.get(nameKey(stored)), stored);
    }
    const central = (stored.id ? lookup?.byId.get(stored.id) : undefined) ?? lookup?.byName.get(nameKey(stored.name));
    return central ? vocabularyName(central, this.language) : vocabularyName(stored, this.language);
  }

  mainFocusName(stored: string | null | undefined): string {
    return this.resolved(this.lookup('mainFocus')?.byName.get(nameKey(stored)), stored);
  }

  methodName(stored: string | null | undefined): string {
    return this.resolved(this.lookup('method')?.byName.get(nameKey(stored)), stored);
  }

  skillName(skill: BilingualSkill | null | undefined): string {
    return skillName(skill, this.language);
  }

  skillCategory(skill: BilingualSkill | null | undefined): string {
    return skillCategory(skill, this.language);
  }

  /** „Kategorie - Fähigkeit“, wie in den Auswahllisten. */
  skillLabel(skill: BilingualSkill | null | undefined): string {
    const category = this.skillCategory(skill);
    return category ? `${category} - ${this.skillName(skill)}` : this.skillName(skill);
  }

  display(value: StoredIndustry | BilingualSkill | null | undefined, kind: VocabularyKind): string {
    switch (kind) {
      case 'industry':
        return this.industryName(value as StoredIndustry);
      case 'mainFocus':
        return this.mainFocusName(value as string);
      case 'method':
        return this.methodName(value as string);
      case 'skill':
        return this.skillName(value as BilingualSkill);
      case 'skillCategory':
        return this.skillCategory(value as BilingualSkill);
    }
  }

  sort<T>(items: readonly T[], displayName: (item: T) => string): T[] {
    return sortByDisplayName(items, displayName, this.language);
  }

  private resolved(central: BilingualName | undefined, stored: string | null | undefined): string {
    return central ? inLanguage(central.name, central.nameEn, this.language) || (stored ?? '') : (stored ?? '');
  }

  /** Lädt die zentrale Liste beim ersten Bedarf; bis sie da ist, gilt der gespeicherte Name. */
  private lookup(kind: 'industry' | 'mainFocus' | 'method'): Lookup | undefined {
    if (!this.lookups.has(kind)) {
      this.lookups.set(kind, lookupOf([]));
      // Kommt die Liste synchron aus dem Cache, hat der Aufrufer schon den richtigen Namen; eine Meldung
      // mitten in der Change Detection würde nur NG0100 auslösen.
      let synchronous = true;
      this.load(kind).subscribe(entries => {
        this.lookups.set(kind, lookupOf(entries));
        if (!synchronous && entries.length) this.loaded.next();
      });
      synchronous = false;
    }
    return this.lookups.get(kind);
  }

  private load(kind: 'industry' | 'mainFocus' | 'method'): Observable<BilingualName[]> {
    switch (kind) {
      case 'industry':
        return this.requestsService.getApproved$(RequestType.INDUSTRY);
      case 'mainFocus':
        return this.requestsService.getApproved$(RequestType.MAINFOCUS);
      case 'method':
        return this.skillService.query(METHOD_CATEGORY).pipe(
          map(page => (page?.content ?? []).map(toMethodEntry)),
          catchError(() => of([])),
        );
    }
  }
}

function toMethodEntry(skill: IFaehigkeit): Requests {
  return {id: skill.id, name: skill.faehigkeit, nameEn: skill.faehigkeitEn};
}
